/** @jsx jsx */

import React, {
  Fragment as _,
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useMemo,
  memo,
} from 'react';
import { css, jsx } from '@emotion/core';
import { useSelector, connect } from 'react-redux';
import placeholder from 'assets/addImg@3x.svg';
import { values, colors } from 'styles/variables';
import getBlockById from 'services/apiHelpers/getBlockById';
import { EditWrapper0, EditWrapper1, EditWrapper2 } from './EditWrappers';
import useImageLoader from 'hooks/useImageLoader';
import LoadingSpinner from 'components/UI/LoadingSpinner';
import checkImgSrcIsGif from 'services/checkImgSrcIsGif';
import Circle from './Circle';

const { headerHeight } = values;
const { lPink, lGrey4 } = colors;

const cachedSrcs = {};

const cachedSrc = (id, src = null) => {
  if (src) {
    cachedSrcs[id] = src;
    return;
  }
  return cachedSrcs[id];
};

const getCalculatedHeight = (
  imageWidth,
  imageHeight,
  maxHeight = 50,
  containerWidth
) => {
  const ratio = imageWidth / imageHeight;
  const calculatedMaxHeight = window.innerHeight * (maxHeight / 100); // window.innerHeight / 2;
  let calculatedHeight = containerWidth / ratio;

  if (calculatedHeight > calculatedMaxHeight) {
    calculatedHeight = calculatedMaxHeight;
  }

  return calculatedHeight;
};

interface IEditable {}

const Image = props => {
  const {
    id,
    mediaParentId = null,
    // focusedNodeId,
    editWrapperType = 0,
    editWrapperSize = '50%',
    editWrapperUseIcon = true,
    padding = 0,
    usePlaceholderValue = false,
    maxHeight = 50,
  } = props;

  // console.log({
  //   editWrapperType,
  //   usePlaceholderValue,
  //   maxHeight,
  // });

  const block = getBlockById(id);

  const focusedNodeId = useSelector((state: any) => state.focusedNodeId);

  let {
    src = placeholder,
    borderStyle = 'rectangle',
    fit = 'contain',
    fixedHeight = false,
    fixedHeightValue = 350,
    imgSize = 0,
    description,
    layout = '',
    width = 800,
    height = 600,
  } = (block && block.props) || ({} as any);

  src = useMemo(() => {
    if (window.dev()) {
      return src;
    }

    return checkImgSrcIsGif(src);
  }, [src]);

  const imgLoadedState = useImageLoader(src);
  const selected = focusedNodeId === id;

  const image: any = useRef(null);
  const hasResized = useRef(false);

  useLayoutEffect(() => {
    // return;

    if (!usePlaceholderValue || !image.current) {
      return;
    }

    const targetHeight = getCalculatedHeight(
      width,
      height,
      maxHeight,
      image.current.getBoundingClientRect().width
    );

    image.current.style.height = `${targetHeight}px`;

    const onResize = () => {
      if (!hasResized.current) {
        image.current.style.height = '';
        //
        window.removeEventListener('resize', onResize);
        hasResized.current = true;
      }
    };

    if (!hasResized.current && imgLoadedState === 2) {
      onResize();
      window.addEventListener('resize', onResize);
    }

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [imgLoadedState]);

  useEffect(() => {
    imgLoadedState === 2 && cachedSrc(id, src);
  }, [imgLoadedState]);

  const EditWrapper = window.dev()
    ? ({ children }) =>
        editWrapperType === 0 ? (
          <EditWrapper0
            id={id}
            mediaParentId={mediaParentId}
            padding={padding}
            selected={selected}
          >
            {children}
          </EditWrapper0>
        ) : (
          <EditWrapper1
            id={id}
            mediaParentId={mediaParentId}
            editWrapperUseIcon={editWrapperUseIcon}
            editWrapperSize={editWrapperSize}
            selected={selected}
          >
            {children}
          </EditWrapper1>
        )
    : ({ children }) => <_>{children}</_>;

  // parsed is not the correct term is it. but. . .
  let parsedImgSize;
  if (imgSize) {
    parsedImgSize = window.innerHeight / 2 + imgSize;
  }

  const noDescription = '';

  // const srcs = useMemo(
  //   () =>
  //     [
  //       {
  //         src:
  //           'https://cdnb.artstation.com/p/assets/images/images/025/613/349/4k/paul-chadeisson-testspeed-0301-bulgarovship-a.jpg?1586357957',
  //         w: 2200,
  //         h: 935,
  //       },
  //       {
  //         src:
  //           'https://cdna.artstation.com/p/assets/images/images/025/429/506/large/roc-h-stickhead200401.jpg?1585762848',
  //         w: 920,
  //         h: 3340,
  //       },

  //       {
  //         src:
  //           'https://cdna.artstation.com/p/assets/images/images/023/299/988/large/brandon-gobey-003.jpg?1578761233',
  //         w: 1920,
  //         h: 865,
  //       },
  //     ][Math.floor(Math.random() * 3)],
  //   []
  // );

  // src = srcs.src;
  // width = srcs.w;
  // height = srcs.h;

  // src =
  //   'http://reviewsite.inspiregroup.co.nz/manual/TEMP/Return%20to%20Work%20opening%20slide%20new.jpg';

  if (borderStyle === 'circle') {
    return (
      <EditWrapper>
        <Circle id={id} src={src} fit={fit} cachedSrc={cachedSrc}>
          <img src={src} alt={description || noDescription} />
        </Circle>
      </EditWrapper>
    );
  }

  // console.log({ fit });

  return (
    <EditWrapper>
      <div
        ref={image}
        css={css`
          width: 100%;
          height: 100%;
          // border: 1px solid lime;
        `}
      >
        {imgLoadedState !== 2 ? (
          <LoadingSpinner />
        ) : (
          <div
            id={id}
            className={src === cachedSrc(id) ? '' : 'fader_-5s'}
            css={css`
						width: 100%;
            height: 100%;
            
						${borderStyle === 'roundedRectangle' &&
              fit === 'contain' &&
              'min-height: auto;'}
							background: url('${src}') no-repeat;
							background-size: ${fit};
							background-position: center;
            ${borderStyle === 'roundedRectangle' && 'border-radius: 50px;'}
            
            // background-position: center;

						img {
              width: 100%;
          
              max-height: ${maxHeight}vh;
							opacity: 0;
            }

            ${parsedImgSize &&
              `
              height: ${parsedImgSize}px;
              img {
                height: ${parsedImgSize}px;
              }

              @media (max-width: ${parsedImgSize}px) {  
                height: 100%;
                img {
                  height: 100%;
                }
              }
              `}

					`}
          >
            <img src={src} alt={description || noDescription} />
          </div>
        )}
      </div>
    </EditWrapper>
  );
};

// export default connect(
//   ({ focusedNodeId }: { focusedNodeId: string | number }, { id }: any) => {
//     return {
//       focusedNodeId,
//     };
//   }
// )(Image);

// moved the focusedNodeId into the component > react-redux useSelector hook
// this makes sure our component updates when src changed
export default props => {
  return <Image key={props.src} {...props} />;
};
